import { z } from "zod";

import { REQUIRED_MESSAGE } from "@/utils";

import { EProductType } from "../products.type";
import { planAdjustmentEntrySchema } from "./auto.schema";
import { baseProductSchema } from "./base-product.schema";
import {
  assistanceProductConfigSchema,
  getDefaultAssistanceProductConfig,
  stripHtml,
} from "./warranty-insurance.schema";

// Schema específico para Assistance
export const assistanceSchema = baseProductSchema
  .omit({ min_permitted_age: true, max_permitted_age: true, policy_number: true, susep_code: true })
  .extend({
    // Campos de idade com valor padrão 1 para Assistance
    min_permitted_age: z
      .preprocess(
        (v) =>
          v === "" || v === null || v === undefined ? 1 : Number(v),
        z.number().min(1, { message: REQUIRED_MESSAGE })
      )
      .default(1),
    max_permitted_age: z
      .preprocess(
        (v) =>
          v === "" || v === null || v === undefined ? 1 : Number(v),
        z.number().min(1, { message: REQUIRED_MESSAGE })
      )
      .default(1),
  // Campos com valor padrão para Assistance
  policy_number: z
    .preprocess(
      (v) => v === "" || v === null || v === undefined ? "N/A" : String(v),
      z.string()
    )
    .default("N/A"),
  susep_code: z
    .preprocess(
      (v) => v === "" || v === null || v === undefined ? "N/A" : String(v),
      z.string()
    )
    .default("N/A"),
  // Campos específicos do Assistance
  policy_holder: z.string().optional().nullable(),
  code_plan: z.string().optional().nullable(),
  secondary_policy_holder: z.string().optional().nullable(),
  table_code: z.string().optional().nullable(),
  insurance_policy_extra_price_code: z.string().optional().nullable(),
  stipulant_code: z.string().optional().nullable(),
  sub_stipulant_code: z.string().optional().nullable(),
  plan_name: z.string().optional().nullable(),

  // Campos específicos de cobertura para Assistance
  coverage_medical_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),
  coverage_dental_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),
  coverage_pharmaceutical_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),
  coverage_hospital_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),
  coverage_emergency_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),
  coverage_funeral_assistance: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),

  // Campos específicos de benefícios para Assistance
  benefit_24h_coverage: z.boolean().optional(),
  benefit_worldwide_coverage: z.boolean().optional(),
  benefit_telemedicine: z.boolean().optional(),
  benefit_second_medical_opinion: z.boolean().optional(),
  benefit_health_checkup: z.boolean().optional(),

  // Campos específicos de rede para Assistance
  network_coverage_national: z.boolean().optional(),
  network_coverage_international: z.boolean().optional(),
  network_hospitals_count: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
  network_clinics_count: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
  network_laboratories_count: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),

  // Campos específicos de limites para Assistance
  annual_limit_per_person: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
  monthly_limit_per_person: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
  consultation_limit_per_month: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
  exam_limit_per_year: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().min(0).optional()
    )
    .optional(),
});

export type TAssistanceFormData = z.infer<typeof assistanceSchema>;

const soulhubAssistanceCapitalRangeSchema =
  baseProductSchema.shape.capital_ranges.element.and(
    z.object({
      new_coverages: z.string().optional(),
      plan_adjustments: z
        .array(planAdjustmentEntrySchema)
        .optional()
        .default([]),
    }),
  );

export const soulhubAssistanceInsuranceSchema = assistanceSchema
  .omit({ capital_ranges: true })
  .extend({
    excluded_risks: z.string().optional(),
    assistance_product_config: assistanceProductConfigSchema,
    capital_ranges: z
      .array(soulhubAssistanceCapitalRangeSchema)
      .nonempty({ message: REQUIRED_MESSAGE }),
  })
  .superRefine((data, ctx) => {
    const w = data.assistance_product_config;
    if (w.commercialization_rules?.includes("age")) {
      if (
        data.min_permitted_age === undefined ||
        data.min_permitted_age === null ||
        data.max_permitted_age === undefined ||
        data.max_permitted_age === null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["min_permitted_age"],
        });
      }
    }
    if (w.emission_type === "apolice" && w.is_collective_policy === true) {
      if (!data.policy_number?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_number"],
        });
      }
      if (!data.policy_start_date || !data.policy_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_start_date"],
        });
      }
    }
    if (w.has_additional_plans) {
      const ranges = data.capital_ranges ?? [];
      if (!ranges.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["capital_ranges"],
        });
      }
      ranges.forEach((range, i) => {
        if (!stripHtml(range.plan_name ?? "")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["capital_ranges", i, "plan_name"],
          });
        }
      });
    }
  });

export type TSoulhubAssistanceInsuranceFormData = z.infer<
  typeof soulhubAssistanceInsuranceSchema
>;

export function createEmptySoulhubAssistanceCapitalRange(): z.infer<
  typeof soulhubAssistanceCapitalRangeSchema
> {
  return {
    capital_value: undefined,
    premium_value: 0,
    net_premium: undefined,
    description: "",
    markup: 0,
    markup_mode: "percentage",
    commission_auto: undefined,
    code_plan: "",
    plan_name: "",
    net_price: undefined,
    minimum_price: undefined,
    maximum_price: undefined,
    new_coverages: "",
    plan_adjustments: [],
  };
}

/** Formulário Assistência SoulHub (aba Dados + Planos em faixas). */
export function getDefaultSoulhubAssistanceInsuranceForm(): TSoulhubAssistanceInsuranceFormData {
  const today = new Date();
  return {
    type: EProductType.INSURANCE_ASSISTANCE,
    name: "",
    insurance_company_id: "",
    policy_number: "N/A",
    cod_sub: "",
    susep_code: "N/A",
    iof_value: 0,
    type_of_vigency: "annual",
    policy_start_date: today,
    policy_end_date: today,
    min_permitted_age: 1,
    max_permitted_age: 1,
    description: "",
    insurance_coverages: "",
    insurance_benefits: "",
    general_conditions: "",
    excluded_risks: "",
    assistance_product_config: getDefaultAssistanceProductConfig(),
    capital_ranges: [createEmptySoulhubAssistanceCapitalRange()],
    commission_distribution: [
      {
        agency: 0,
        commission: 0,
        pro_labore: 0,
        markup: undefined,
        markup_mode: "percentage",
      },
    ],
    commission_lifetime_other_installments: 0,
    pro_labore_lifetime_other_installments: 0,
    commission_unique: 0,
    pro_labore_unique: 0,
    capital_value: undefined,
    premium_value: undefined,
    net_premium: undefined,
    markup: undefined,
    markup_mode: "percentage",
    insurance_policy_id: "",
    policy_holder: "",
    secondary_policy_holder: "",
    table_code: "",
    insurance_policy_extra_price_code: "",
    stipulant_code: "",
    sub_stipulant_code: "",
    code_plan: "",
    plan_name: "",
    external_product_id: "",
  } as TSoulhubAssistanceInsuranceFormData;
}
